import _ from 'lodash';
import raw from 'raw.macro';
import * as http from './http';
import * as utils from './utils';
import events from './Events';

import Project from './Project';
import Subject from './Subject';
import Derivative from './Derivative';
import Experiment from './Experiment';
import Resource from './Resource';

import dgsm from '../data/dgsm.json';

const searchCore = raw('./search-core.xml');
const searchFullLab = raw('./search-full-lab.xml');
const searchFullCases = raw('./search-full-cases.xml');
const searchFullCore = raw('./search-full-core.xml');
const searchSubjects = raw('./search-subjects.xml');
const searchCases = raw('./search-cases.xml');
const searchStaff = raw('./search-staff.xml');
const searchEquipment = raw('./search-equipment.xml');
const searchTherapies = raw('./search-therapies.xml');
const searchReviews = raw('./search-reviews.xml');
const searchPSG = raw('./search-psg.xml');
const searchUsers = raw('./search-users.xml');


export default class XNAT {

   Project = Project
   Subject = Subject
   Derivative = Derivative
   Experiment = Experiment
   Resource = Resource

   urls = {
      session: '{host}/JSESSION', // https://wiki.xnat.org/pages/viewpage.action?pageId=6226264
      auth: '{host}/auth',
   }

   getProjects = utils.getChildren(Project)
   createProject = utils.createChild(Project)

   constructor(host, username, password) { // eslint-disable-line consistent-return
      if (!host) {
         throw new Error('Missing one or more parameter.');
      }

      this.on = events.on;

      this.data = { host };

      if (username && password) {
         return this.login(username, password);
      }
   }

   login(username, password) {
      if (!username || !password) {
         throw new Error('Missing one or more parameter.');
      }

      const url = utils.tpl(this.urls.session, this.data);
      const credentials = window.btoa(`${username}:${password}`);

      return http.get(url, {
         headers: {
            Authorization: `Basic ${credentials}`,
         },
      }).then(session => this); // eslint-disable-line no-unused-vars
   }

   logout() {
      const url = utils.tpl(this.urls.session, this.data);
      return http.del(url)
         .then(_.stubTrue, _.stubTrue); // always succeeds
   }

   reset(username) {
      const url = utils.tpl('{host}/../../password/request', this.data);
      const options = {
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ username }),
      };
      return http.postJSON(url, options);
   }

   resetPassword(token, password) {
      const url = utils.tpl('{host}/../../password/set', this.data);
      const options = {
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ token, password }),
      };
      return http.postJSON(url, options);
   }

   checkLogin() {
      const url = utils.tpl(this.urls.auth, this.data);
      return http.get(url, { redirect: 'manual' })
         .then(response => response.ok)
         .catch(() => false);
   }

   checkIfHostIsReachable() {
      const url = utils.tpl(`{host}/../favicon.ico?_=${Date.now()}`, this.data);
      const timeout = new Promise((resolve, reject) => window.setTimeout(reject, 3000));

      return Promise.race([http.get(url), timeout])
         .then(() => true, () => false);
   }

   getSession() {
      const url = utils.tpl(this.urls.session, this.data);
      return http.get(url).then(response => response.text());
   }

   renewSession() {
      const url = utils.tpl('{host}/version', this.data);
      return http.get(url).then(
         () => true,
         () => false, // Fehlermeldung übergeben?
      );
   }

   sendEmail(template, recipients) {
      if (process.env.NODE_ENV !== 'production') {
         console.info('SEND EMAIL', template, recipients);
         return Promise.resolve();
      }

      // only bcc dgsm if it's not already a recipient
      const bcc = recipients.find(r => r.email === dgsm.email) ? undefined : dgsm;

      return this.getSession()
         .then((jsessionid) => {
            const url = utils.tpl('{host}/../../postman/', this.data);
            const body = {
               jsessionid,
               template,
               recipients,
               bcc,
               reply_to: dgsm,
            };
            const options = {
               headers: { 'Content-Type': 'application/json' },
               body: JSON.stringify(body),
            };
            /* TODO
               mögliche fehler:
                  - im `content` waren variablen enthalten, die es nicht gibt
                  - nicht alle mails konten versendet werden
             */
            return http.postJSON(url, options);
         });
   }

   // { login, email, firstname, lastname, enabled, verified, userAuths: [{ authId, method }] }
   getUser(handle) {
      const url = utils.tpl(`{host}/user/${handle}`, this.data);
      return http.getJSON(url);
   }

   // `xapi`-Variante enthält keine Gruppen, darum umständlich über die Suche
   // [ login: { id, isEnabled, login, firstname, lastname, email, groups, revProject, slmLab } ]
   getUsers() {
      const renameMap = {
         last: 'lastname',
         first: 'firstname',
         xdat_col_usergroupsgroupid0groupid: 'group',
         xdat_col_userxdat_user_id: 'xdat_user_id',
         xdat_col_userenabled: 'isEnabled',
      };
      return this.search(searchUsers)
         .then((usersData) => {
            const users = {};

            _.each(usersData, (userData) => {
               let user = utils.rename(userData, renameMap);
               const { login, group } = user;

               if (users[login]) { // already exists, just add the new group
                  users[login].groups.push(group);
               }
               else { // not yet exists, clean and add
                  user = _.mapValues(user, utils.decodeHtmlEntities);
                  user.groups = group ? [group] : [];
                  user.isEnabled = user.isEnabled === '1'; // cast string to bool
                  delete user.quarantine_status;
                  delete user.group;
                  users[login] = user;
               }

               if (/^rev_\w+?_reviewer$/.test(group)) { // e.g. rev_0027_reviewer
                  users[login].revProject = group.replace('_reviewer', '');
               }
               else if (/^lab_\w+?_slm$/.test(group)) { // e.g. lab_348AFE8733924546BA3E19FE9FC935E4_slm
                  users[login].slmLab = group.replace('_slm', '');
               }
            });

            // mark accounts wich have an associated rm-account and delete the latter
            _.each(users, (user) => {
               if (/^\w+?_rm$/.test(user.login)) { // e.g. mustermann_rm
                  const associatedLogin = user.login.replace('_rm', '');
                  if (users[associatedLogin]) {
                     users[associatedLogin].rmID = user.xdat_user_id;
                     delete users[user.login];
                  }
               }
            });

            return users;
         });
   }

   saveUser(data) {
      const url = utils.tpl('{host}/../app/action/ModifyUser', this.data);
      const formData = new window.FormData();

      if (data.xdat_user_id == null) delete data.xdat_user_id;

      _.each(data, (value, key) => formData.append(`xdat:user.${key}`, value));

      return http.post(url, {
         header: {
            'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
         },
         body: formData,
      });
   }

   toggleUserActivationState(login) {
      const url = utils.tpl(`{host}/../app/action/XDATActionRouter/xdataction/enable/search_element/xdat%3Auser/search_field/xdat%3Auser.login/search_value/${login}`, this.data);
      return http.get(url);
   }

   whoami() {
      const url = utils.tpl(this.urls.auth, this.data);
      const pattern = /User '(\w+)' is logged in/; // hard coded in UserAuth.java
      return http.getText(url)
         .then(text => _.last(text.match(pattern)))
         .catch(() => null);
   }

   checkForAdmin(username) {
      const url = utils.tpl(`{host}/../xapi/users/${username}/roles`, this.data);
      return http.getJSON(url)
         .then(roles => roles.includes('Administrator'))
         .catch(() => null);
   }

   addToGroup(project, group, username) {
      if (!project || !group || !username) return Promise.reject(new Error('missing parameter'));
      return http.put(`${this.data.host}/projects/${project}/users/${group}/${username}/`);
   }

   removeFromGroup(project, group, username) {
      if (!project || !group || !username) return Promise.reject(new Error('missing parameter'));
      return http.del(`${this.data.host}/projects/${project}/users/${group}/${username}/`);
   }

   search(xml, data) {
      const url = '{host}/search/?format=json';
      return http.postJSON(utils.tpl(url, this.data), { body: utils.tpl(xml, data) });
   }

   getSleepLabs(coreType = searchCore) {
      const renameMap = {
         sncs_col_coredataversion: 'version',
         sncs_dgsmdata_dgsmid: 'dgsmid',
      };
      return this.search(coreType)
         .then(labs => _.uniqBy(labs, 'project').map((lab) => {
            let labData = utils.rename(lab, renameMap);
            labData = _.mapValues(labData, utils.decodeHtmlEntities);
            return new Project(_.assign(labData, this.data)); // parse JSON blobs to XNAT Projects
         }));
   }

   getSubjects(year) {
      const YEAR = year || new Date().getFullYear();
      const renameMap = {
         xnat_col_subjectdatalabel: 'label',
         subjectid: 'subject',
      };
      return this.search(searchSubjects, { YEAR })
         .then(cases => cases.filter(revCase => revCase.xnat_col_subjectdatalabel.startsWith(YEAR)))
         .then(cases => _.map(cases, (revCase) => {
            const caseData = utils.rename(revCase, renameMap);
            return new Subject(_.assign(caseData, this.data));
         }));
   }

   getCore() {
      return this.getSleepLabs(searchFullCore);
   }

   getFullLab() {
      const emptyisch = { '': true, '-1': true, '-1.0': true };
      const decode = value => (emptyisch[value] ? '' : utils.decodeHtmlEntities(value));
      const rename = (value, key) => key.substr(key.lastIndexOf('_') + 1).replace(/^(cases|core)data/, '');
      const clear = lab => _.mapValues(_.mapKeys(lab, rename), decode);
      const merge = ([labs, cases]) => {
         const caseMap = _.keyBy(cases, 'project');
         _.each(labs, lab => Object.assign(lab, caseMap[lab.project]));
         return labs;
      };

      return Promise.all([
         this.search(searchFullLab),
         this.search(searchFullCases)
      ])
         .then(merge)
         .then(labs => _.map(labs, clear));
   }

   getCases() {
      const renameMap = {
         snrev_col_casedatalabel: 'label',
         subject_id: 'subject',
         expt_id: 'experiment',
      };
      return this.search(searchCases)
         .then(cases => _.map(cases, (revCase) => {
            const caseData = utils.rename(revCase, renameMap);
            return new Experiment(_.assign(caseData, this.data));
         }));
   }

   getReviews() {
      const renameMap = {
         snrev_col_reviewdatalabel: 'label',
         subject_id: 'subject',
         expt_id: 'experiment',
         snrev_col_reviewdatarecommendationtextcomment: 'textComment',
         snrev_col_reviewdatainvestigatorfirstname: 'revLogin',
         snrev_col_reviewdatainvestigatorlastname: 'revProject',
         recommendation_comment: 'comment',
         recommendation_isrevisitationrecommended: 'isRevisitationRecommended',
         recommendation_patientabbreviation: 'patientAbbreviation',
         recommendation_patientage: 'patientAge',
         recommendation_patientyearofbirth: 'patientYearOfBirth',
         recommendation_score: 'score',
         recommendation_textcomment: 'textComment',
      };
      return this.search(searchReviews)
         .then(reviews => _.map(reviews, (review) => {
            let reviewData = utils.rename(review, renameMap);
            reviewData = _.mapValues(reviewData, utils.decodeHtmlEntities);
            return new Experiment(_.assign(reviewData, this.data));
         }));
   }

   /*
      Loads reviewers and their associated cases
      reviewer = { revProject, cases = [], GROUP_ID, email, lastname, login, firstname, displayname, numberOfReviews }
    */
   getReviewers() {
      return this.getProjects()
         .then((projects) => {
            const revProjects = projects.filter(project => project.data.project.startsWith('rev_'));
            return Promise.all(_.map(revProjects, revProject =>
               Promise.all([
                  revProject.getUsers(),
                  revProject.getExperiments(),
               ])
                  .then(([users, cases]) => {
                     const user = _.find(users, { displayname: 'reviewer' });
                     return _.assign({ revProject }, { cases }, user);
                  })
            ));
         });
   }

   getPSGInfo() {
      const renameMap = {
         sncs_premisesdata_countbedsmonitoring: 'countbedsmonitoring',
         sncs_premisesdata_countbedspsg: 'countbedspsg',
         sncs_dgsmdata_dgsmid: 'dgsmid',
         sncs_casesdata_countpg: 'countpg',
         sncs_coredata_sncs_dgsmdata_diff: 'diff',
         sncs_casesdata_countmslt: 'countmslt',
         sncs_coredata_sncs_premisesdata_diff: 'diff',
         sncs_coredata_sncs_casesdata_diff: 'diff',
         sncs_casesdata_countpsg: 'countpsg',
         sncs_casesdata_countmwt: 'countmwt',
         sncs_premisesdata_countbedspg: 'countbedspg',
      };
      return this.search(searchPSG)
         .then(labs => _.map(labs, (lab) => {
            let labData = utils.rename(lab, renameMap);
            labData = _.mapValues(labData, value => (value === '-1' ? '' : value));
            labData.hospital = utils.decodeHtmlEntities(labData.hospital);
            return new Project(_.assign(labData, this.data));
         }));
   }

   getEquipment() {
      return Promise.all([
         this.search(searchEquipment),
         this.search(searchCore),
      ])
         .then(([equipmentData, coreData]) => { // cleaning and merging data
            const labs = _.keyBy(this.cleanLabData(coreData), 'project');
            const rename = (value, key) => key.replace('sncs_col_equipmentdatadevice0', '');

            _.map(equipmentData, (device) => {
               const lab = labs[device.project];

               if (!lab.equipment) lab.equipment = [];

               let cleanDevice = _.mapKeys(device, rename);
               cleanDevice = _.mapValues(cleanDevice, utils.decodeHtmlEntities);
               delete cleanDevice.quarantine_status;
               delete cleanDevice.project;

               lab.equipment.push(cleanDevice);
            });

            return _.map(labs, lab => new Project(_.assign(lab, this.data)));
         });
   }

   getTherapies() {
      return Promise.all([
         this.search(searchTherapies),
         this.search(searchCore),
      ])
         .then(([therapyData, coreData]) => { // cleaning and merging data
            const labs = _.keyBy(this.cleanLabData(coreData), 'project');
            const rename = (value, key) => key.replace('sncs_col_casesdata', '');

            _.forEach(therapyData, therapy => {
               const lab = labs[therapy.project];
               let cleanTherapy = _.mapKeys(therapy, rename);
               Object.assign(lab, cleanTherapy);
            })

            return _.map(labs, lab => new Project(_.assign(lab, this.data)));
         });
   }

   getStaff() {
      return Promise.all([
         this.search(searchStaff),
         this.search(searchCore),
      ])
         .then(([staffData, coreData]) => { // cleaning and merging data
            const labs = _.keyBy(this.cleanLabData(coreData), 'project');
            const rename = (value, key) => key.replace('sncs_col_staffdataperson0', '');

            _.map(staffData, (employee) => {
               const lab = labs[employee.project];

               if (!lab.staff) lab.staff = [];

               let cleanEmployee = _.mapKeys(employee, rename);
               cleanEmployee = _.mapValues(cleanEmployee, utils.decodeHtmlEntities);
               delete cleanEmployee.quarantine_status;
               delete cleanEmployee.project;

               lab.staff.push(cleanEmployee);
            });

            return _.map(labs, lab => new Project(_.assign(lab, this.data)));
         });
   }

   cleanLabData = (labs) => {
      const labMap = { sncs_dgsmdata_dgsmid: 'dgsmid' };

      return _.map(labs, (lab) => {
         let cleanLab = utils.rename(lab, labMap);
         cleanLab = _.mapValues(cleanLab, utils.decodeHtmlEntities);
         delete cleanLab.sncs_col_coredataversion;
         delete cleanLab.sncs_coredata_sncs_dgsmdata_diff;
         delete cleanLab.quarantine_status;
         return cleanLab;
      });
   }

   setupReviewer(username) {
      const revProject = `rev_${Date.now()}`;
      const revGroup = 'reviewer';
      const fullGroupName = `${revProject}_${revGroup}`;
      const start = `${this.data.host}/projects/${revProject}`;

      const addToGroup = () => this.addToGroup(revProject, fullGroupName, username);
      const createProject = () => http.put(`${start}/?xnat:projectData/name=${revProject}&xnat:projectData/secondary_ID=${revProject}&accessibility=private`);
      const createSubject = () => http.put(`${start}/subjects/reviews/`);
      const createGroup = () => http.put([
         `${start}/groups/${revGroup}/?xdat:userGroup/displayName=${revGroup}`,
         `xnat:projectData_xnat:projectData/ID_${revProject}_R=1`,
         `xnat:subjectData_xnat:subjectData/project_${revProject}_R=1`,
         `snrev:caseData_snrev:caseData/project_${revProject}_wasSet=1`,
         `snrev:caseData_snrev:caseData/project_${revProject}_R=1`,
         `snrev:reviewData_snrev:reviewData/project_${revProject}_wasSet=1`,
         `snrev:reviewData_snrev:reviewData/project_${revProject}_R=1`,
         `snrev:reviewData_snrev:reviewData/project_${revProject}_E=1`,
         `snrev:reviewData_snrev:reviewData/project_${revProject}_D=1`,
      ].join('&'));

      return Promise.resolve()
         .then(createProject)
         .then(createGroup)
         .then(addToGroup)
         .then(createSubject);
   }

   createSleepLab(hospital, sleeplab, dgsmid) {
      const labID = `lab_${Date.now()}`;
      const group = 'slm';
      const start = `${this.data.host}/projects/${labID}`;

      const createProject = () => http.put(`${start}?xnat:projectData/name=${labID}&xnat:projectData/secondary_ID=${labID}&accessibility=private`);
      const createCoversheet = () => http.put(`${start}/subjects/coversheet/`);
      const createDgsmData = () => http.put(`${start}/subjects/coversheet/experiments/dgsm/?sncs:dgsmData&sncs:dgsmData/dgsmId=${dgsmid}`);
      const createCoreData = () => http.put(`${start}/subjects/coversheet/experiments/core/?sncs:coreData&sncs:coreData/hospital=${hospital}&sncs:coreData/sleeplab=${sleeplab}&sncs:coreData/version=0`);
      const createSlmGroup = () => http.put([
         `${start}/groups/${group}/?xdat:userGroup/displayName=${group}`,
         `xdat:userGroup/tag=${labID}`,
         `xnat:projectData_xnat:projectData/ID_${labID}_R=1`,
         `xnat:subjectData_xnat:subjectData/project_${labID}_R=1`,
         `sncs:casesData_sncs:casesData/project_${labID}_D=1`,
         `sncs:casesData_sncs:casesData/project_${labID}_E=1`,
         `sncs:casesData_sncs:casesData/project_${labID}_R=1`,
         `sncs:coreData_sncs:coreData/project_${labID}_D=1`,
         `sncs:coreData_sncs:coreData/project_${labID}_E=1`,
         `sncs:coreData_sncs:coreData/project_${labID}_R=1`,
         `sncs:dgsmData_sncs:dgsmData/project_${labID}_R=1`,
         `sncs:equipmentData_sncs:equipmentData/project_${labID}_D=1`,
         `sncs:equipmentData_sncs:equipmentData/project_${labID}_E=1`,
         `sncs:equipmentData_sncs:equipmentData/project_${labID}_R=1`,
         `sncs:premisesData_sncs:premisesData/project_${labID}_D=1`,
         `sncs:premisesData_sncs:premisesData/project_${labID}_E=1`,
         `sncs:premisesData_sncs:premisesData/project_${labID}_R=1`,
         `sncs:staffData_sncs:staffData/project_${labID}_D=1`,
         `sncs:staffData_sncs:staffData/project_${labID}_E=1`,
         `sncs:staffData_sncs:staffData/project_${labID}_R=1`,
      ].join('&'));

      return Promise.resolve()
         .then(createProject)
         .then(createCoversheet)
         .then(createDgsmData)
         .then(createCoreData)
         .then(createSlmGroup);
   }

}
